import { useState } from 'react';
import { Button, Card, Dialog, Icon, Input } from '@/ds';
import { ApiError } from '@/api/client';
import { useOperation, useProfile, type CurrentUser } from '@/api/hooks';
import { useControlSize } from '@/lib/useControlSize';

function SectionTitle({ children }: { children: string }) {
  return (
    <div
      style={{
        font: '500 16px var(--font-display)',
        letterSpacing: 'var(--ls-heading)',
        color: 'var(--text-strong)',
      }}
    >
      {children}
    </div>
  );
}

function Hint({ children }: { children: string }) {
  return (
    <p style={{ font: '400 13px/1.5 var(--font-text)', color: 'var(--text-muted)', maxWidth: 460 }}>
      {children}
    </p>
  );
}

function errorMessage(error: unknown): string | null {
  return error instanceof ApiError ? error.message : null;
}

export function ProfileTab({ user }: { user: CurrentUser }) {
  const profile = useProfile(true);
  const rename = useOperation<{ name: string }>('profile.rename');
  const setPassword = useOperation<{ currentPassword?: string; newPassword: string }>(
    'profile.set_password',
  );
  const clearPassword = useOperation<{ currentPassword: string }>('profile.clear_password');
  const revokeAll = useOperation<Record<string, never>>('session.revoke_all');
  const size = useControlSize();

  const [name, setName] = useState(user.name);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const activeSessions = profile.data?.activeSessions ?? 0;

  function submitPassword() {
    setLocalError(null);
    if (newPassword !== repeatPassword) {
      setLocalError('Пароли не совпадают');
      return;
    }
    setPassword.mutate(
      user.hasPassword ? { currentPassword, newPassword } : { newPassword },
      {
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setRepeatPassword('');
        },
      },
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 620 }}>
      <Card padding="lg">
        <SectionTitle>Кто вы</SectionTitle>
        <div
          style={{
            marginTop: 'var(--space-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-5)',
          }}
        >
          <Input
            size={size}
            label="Имя"
            value={name}
            onChange={(e) => setName(e.target.value)}
            help="Показывается в журнале действий и в списке «кто чем занят»"
            {...(errorMessage(rename.error) ? { error: errorMessage(rename.error) as string } : {})}
          />
          <div>
            <div style={{ font: '400 12px var(--font-text)', color: 'var(--text-subtle)' }}>
              Рабочая почта
            </div>
            <div
              style={{
                marginTop: 'var(--space-2)',
                font: '400 14px var(--font-mono)',
                color: 'var(--text-body)',
              }}
            >
              {user.email}
            </div>
            <div
              style={{
                marginTop: 'var(--space-2)',
                font: '400 12px var(--font-text)',
                color: 'var(--text-subtle)',
              }}
            >
              Адрес меняется в конфигурации сервера, не здесь
            </div>
          </div>
          <div>
            <Button
              size={size}
              onClick={() => rename.mutate({ name })}
              disabled={name.trim() === user.name || name.trim().length === 0}
              loading={rename.isPending}
            >
              Сохранить имя
            </Button>
          </div>
        </div>
      </Card>

      <Card padding="lg">
        <SectionTitle>Резервный вход по паролю</SectionTitle>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Hint>
            {user.hasPassword
              ? 'Пароль нужен, когда Google недоступен. Не короче двенадцати символов.'
              : 'Пароль пока не задан. Он понадобится, если Google окажется недоступен. Не короче двенадцати символов.'}
          </Hint>
        </div>

        <div
          style={{
            marginTop: 'var(--space-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-5)',
          }}
        >
          {user.hasPassword ? (
            <Input
              size={size}
              label="Текущий пароль"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          ) : null}
          <Input
            size={size}
            label="Новый пароль"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <Input
            size={size}
            label="Повторите пароль"
            type="password"
            value={repeatPassword}
            onChange={(e) => setRepeatPassword(e.target.value)}
            {...(localError ?? errorMessage(setPassword.error)
              ? { error: (localError ?? errorMessage(setPassword.error)) as string }
              : {})}
          />
          <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <Button
              size={size}
              onClick={submitPassword}
              disabled={newPassword.length === 0}
              loading={setPassword.isPending}
            >
              {user.hasPassword ? 'Изменить пароль' : 'Задать пароль'}
            </Button>
            {user.hasPassword ? (
              <Button variant="ghost" size={size} onClick={() => setConfirmClear(true)}>
                Отключить вход по паролю
              </Button>
            ) : null}
          </div>
          {setPassword.isSuccess ? (
            <div style={{ font: '400 13px var(--font-text)', color: 'var(--green-600)' }}>
              Пароль сохранён
            </div>
          ) : null}
        </div>
      </Card>

      <Card padding="lg">
        <SectionTitle>Сессии</SectionTitle>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Hint>
            {`Активных сессий: ${activeSessions}. Выход на всех устройствах завершит их все, включая текущее.`}
          </Hint>
        </div>
        <div style={{ marginTop: 'var(--space-5)' }}>
          <Button
            variant="secondary"
            size={size}
            onClick={() => setConfirmRevoke(true)}
            iconLeft={<Icon name="LogOut" size={16} />}
          >
            Выйти на всех устройствах
          </Button>
        </div>
      </Card>

      <Dialog
        open={confirmRevoke}
        title="Выйти на всех устройствах"
        description="Все сессии будут завершены, включая эту. Войти придётся заново."
        onClose={() => setConfirmRevoke(false)}
        footer={
          <>
            <Button variant="secondary" size={size} onClick={() => setConfirmRevoke(false)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              size={size}
              loading={revokeAll.isPending}
              onClick={() => {
                revokeAll.mutate({}, { onSuccess: () => setConfirmRevoke(false) });
              }}
            >
              Выйти везде
            </Button>
          </>
        }
      />

      <Dialog
        open={confirmClear}
        title="Отключить вход по паролю"
        description="Останется только вход через Google. Если Google окажется недоступен, войти будет нельзя."
        onClose={() => setConfirmClear(false)}
        footer={
          <>
            <Button variant="secondary" size={size} onClick={() => setConfirmClear(false)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              size={size}
              loading={clearPassword.isPending}
              onClick={() => {
                clearPassword.mutate(
                  { currentPassword },
                  { onSuccess: () => setConfirmClear(false) },
                );
              }}
            >
              Отключить
            </Button>
          </>
        }
      >
        <Input
          size={size}
          label="Текущий пароль"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          {...(errorMessage(clearPassword.error)
            ? { error: errorMessage(clearPassword.error) as string }
            : {})}
        />
      </Dialog>
    </div>
  );
}
